import { Box, ButtonBase, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useState } from 'react';
import { ReportGroupAsset } from '@/hooks/api/reports/useListReportGroupAssets';
import { getAssetFileName, getAssetFolderDirectory } from '@/utils/assets';
import { Thumbnail } from '@/components/Thumbnail';
import { useReportMasterDetail } from './ReportMasterDetailContext';

export const ReportBreadcrumbToolbar = ({
  assets,
  selectedAssetId,
  groupName,
  toolbarActions,
}: {
  assets: ReportGroupAsset[];
  selectedAssetId: string | undefined;
  groupName: string;
  toolbarActions?: React.ReactNode;
}) => {
  const { setSelectedAssetId } = useReportMasterDetail();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | undefined>();

  const seen = new Map<string, ReportGroupAsset>();
  for (const asset of assets) {
    if (!seen.has(asset.id)) {
      seen.set(asset.id, asset);
    }
  }
  const assetList = [...seen.values()];

  const effectiveSelectedId = selectedAssetId ?? assetList[0]?.id;
  const selectedAsset = assetList.find(a => a.id === effectiveSelectedId);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          padding: '4px 8px',
          gap: 0.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
          {selectedAsset && (
            <>
              <ButtonBase
                onClick={event => setMenuAnchor(event.currentTarget)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  borderRadius: '4px',
                  padding: '2px 4px',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Typography variant='body2' noWrap sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {groupName}
                </Typography>
              </ButtonBase>
              <ChevronRight sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
              <Tooltip title={selectedAsset.path} enterDelay={500}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', minWidth: 0, overflow: 'hidden' }}>
                  {getAssetFolderDirectory(selectedAsset) && (
                    <Typography
                      variant='caption'
                      noWrap
                      sx={{ color: 'text.secondary', flexShrink: 1, minWidth: 0, mr: 0.5 }}
                    >
                      {getAssetFolderDirectory(selectedAsset)}/
                    </Typography>
                  )}
                  <Typography variant='body2' noWrap sx={{ color: 'text.primary', fontWeight: 600, flexShrink: 0 }}>
                    {getAssetFileName(selectedAsset)}
                  </Typography>
                </Box>
              </Tooltip>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>{toolbarActions}</Box>
      </Box>
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(undefined)}
        slotProps={{ paper: { sx: { maxHeight: 400, minWidth: 200 } } }}
      >
        {assetList.map(asset => (
          <MenuItem
            key={asset.id}
            selected={asset.id === effectiveSelectedId}
            onClick={() => {
              setSelectedAssetId(asset.id);
              setMenuAnchor(undefined);
            }}
            sx={{ gap: 1, py: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 'unset' }}>
              <Thumbnail asset={asset} size={24} noLink />
            </ListItemIcon>
            <ListItemText
              primary={getAssetFileName(asset)}
              primaryTypographyProps={{ variant: 'body2', noWrap: true }}
            />
            <Typography variant='caption' sx={{ color: 'text.secondary', ml: 1, flexShrink: 0 }}>
              {asset.matches?.length ?? 0}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
